import { useCallback, useRef, useState, type ReactNode } from "react";
import { AppHeader } from "./AppHeader.js";
import type { PozycjaMenu } from "./pozycjeMenu.js";
import { Sidebar } from "./Sidebar.js";
import { useOutsideDismiss } from "./useOutsideDismiss.js";
import { useStartStronyPoZmianieTrasy } from "./useStartStronyPoZmianieTrasy.js";

/**
 * Powloka aplikacji wg §4.
 *
 * SCROLL LEZY NA DOKUMENCIE, a header i sidebar sa position: fixed. Nie robimy
 * <main> z wlasnym overflow-y: taki kontener przycina focus ring pierwszego
 * i ostatniego dziecka przy krawedzi paddingu, co lamie A-1 i jest stala pulapka
 * na kazda przyszla zmiane paddingu.
 *
 * Warstwa wygaszania (L-3a) jest osobnym, pustym elementem - opis mechanizmu
 * przy .scroll-fade w styles/shell.css.
 */

interface Props {
  username: string;
  onSignOut: () => void | Promise<void>;
  /** Pozycje menu bocznego wg roli konta (pozycjeMenu.ts). Pominiete: lista administratora. */
  pozycje?: readonly PozycjaMenu[];
  children: ReactNode;
}

export function AppShell({ username, onSignOut, pozycje, children }: Props): React.JSX.Element {
  const [menuOtwarte, setMenuOtwarte] = useState(false);
  const refHamburgera = useRef<HTMLButtonElement>(null);
  const refSidebara = useRef<HTMLElement>(null);
  const refGlownego = useRef<HTMLElement>(null);

  useStartStronyPoZmianieTrasy(refGlownego);

  const zamknijMenu = useCallback((zwrocFokus: boolean) => {
    setMenuOtwarte(false);
    // A-4: Escape zwraca fokus na wyzwalacz. Kanoniczny gotowiec tego NIE robi dla
    // kafelka nawigacji (robi tylko dla menu konta) - to jego brak, nie wzorzec.
    if (zwrocFokus) refHamburgera.current?.focus();
  }, []);

  // Dwie referencje, bo przycisk i kafelek to rozlaczne poddrzewa DOM: bez tego
  // klik w hamburger liczylby sie jako "klik poza" i menu zamykaloby sie w tej samej
  // chwili, w ktorej sie otwiera.
  useOutsideDismiss(menuOtwarte, [refSidebara, refHamburgera], zamknijMenu);

  // Wybor pozycji w OTWARTYM kafelku menu (waski ekran): kafelek zaraz po tym dostaje visibility:hidden, a przegladarka
  // zrzuca wtedy fokus na <body> - dokladnie to, czemu ma zapobiegac przeniesienie fokusu po zmianie trasy. Hook go nie
  // uratuje, bo w chwili zmiany trasy fokus jeszcze siedzi na linku w kafelku, wiec przenosimy go tu, z gory na <main>.
  // Na szerokim ekranie menu jest stale widoczne (menuOtwarte = false) i fokus zostaje na pozycji menu.
  const wybierzPozycjeMenu = (): void => {
    if (menuOtwarte) refGlownego.current?.focus({ preventScroll: true });
    setMenuOtwarte(false);
  };

  return (
    <>
      <AppHeader
        username={username}
        onSignOut={onSignOut}
        menuOtwarte={menuOtwarte}
        onPrzelaczMenu={() => setMenuOtwarte((v) => !v)}
        refHamburgera={refHamburgera}
      />

      <Sidebar otwarte={menuOtwarte} onWybor={wybierzPozycjeMenu} refKontenera={refSidebara} pozycje={pozycje} />

      {/* L-3a: warstwa wygaszania. Pusta i aria-hidden - to czysta dekoracja,
          ktora nie moze trafic do drzewa dostepnosci ani lapac myszy. */}
      <div className="scroll-fade" aria-hidden="true" />

      {/* tabIndex=-1: <main> jest tylko celem PROGRAMOWEGO fokusu po zmianie trasy (patrz
          useStartStronyPoZmianieTrasy) - nie wchodzi do kolejnosci Tab. */}
      <main className="app-main" ref={refGlownego} tabIndex={-1}>
        <div className="app-content">{children}</div>
      </main>
    </>
  );
}
